import React, { useState, useEffect } from 'react';
import { TargetSelect } from './TargetSelect';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const NetworkEnumScanner = () => {
  const [target, setTarget] = useState('');
  const [loading, setLoading] = useState(false);
  const [scans, setScans] = useState([]);
  const [selectedScan, setSelectedScan] = useState(null);
  const [viewMode, setViewMode] = useState('form'); // 'form' | 'results' | 'history'
  const [activeTab, setActiveTab] = useState('summary');

  // Opciones de scan
  const [options, setOptions] = useState({
    port_scan: true,
    smb_enum: true,
    nfs_enum: true,
    timeout: 5
  });

  useEffect(() => {
    fetchScans();
  }, []);

  const fetchScans = async () => {
    try {
      const response = await axios.get(`${API}/scans/network-enum`);
      setScans(response.data);
    } catch (error) {
      console.error("Error fetching Network Enum scans:", error);
    }
  };

  const startScan = async (e) => {
    e.preventDefault();
    if (!target) {
      alert("Por favor ingresa un target (IP o hostname)");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/scans/network-enum`, {
        target: target,
        options: options
      });
      
      setSelectedScan(response.data);
      setViewMode('results');
      fetchScans();
      setTarget('');
    } catch (error) {
      alert("Error ejecutando Network Enumeration: " + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const viewScanDetails = (scan) => {
    setSelectedScan(scan);
    setViewMode('results');
  };

  const getRiskColor = (level) => {
    const colors = {
      'CRITICAL': 'text-red-600 bg-red-100',
      'HIGH': 'text-orange-600 bg-orange-100',
      'MEDIUM': 'text-yellow-600 bg-yellow-100',
      'LOW': 'text-blue-600 bg-blue-100',
      'INFO': 'text-gray-600 bg-gray-100'
    };
    return colors[level] || colors['INFO'];
  };

  const renderSummaryTab = () => {
    if (!selectedScan?.results) return null;
    const results = selectedScan.results;
    const risk = results.risk_assessment || {};

    return (
      <div className="space-y-6">
        {/* Risk Score Card */}
        <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg p-6 text-white">
          <h3 className="text-2xl font-bold mb-2">🎯 Risk Score</h3>
          <div className="flex items-center space-x-4">
            <div className="text-5xl font-bold">{risk.risk_score || 0}</div>
            <div>
              <div className={`text-xl font-semibold px-4 py-2 rounded ${getRiskColor(risk.risk_level)}`}>
                {risk.risk_level || 'N/A'}
              </div>
              <p className="mt-2 text-sm opacity-90">{risk.risk_summary || 'Sin evaluación'}</p>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-blue-500">
            <div className="text-sm text-gray-600">Total Checks</div>
            <div className="text-2xl font-bold text-gray-800">
              {results.summary?.total_checks || 0}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-green-500">
            <div className="text-sm text-gray-600">Successful</div>
            <div className="text-2xl font-bold text-green-600">
              {results.summary?.successful_checks || 0}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-red-500">
            <div className="text-sm text-gray-600">Failed</div>
            <div className="text-2xl font-bold text-red-600">
              {results.summary?.failed_checks || 0}
            </div>
          </div>
        </div>

        {/* Risk Factors */}
        {risk.risk_factors && risk.risk_factors.length > 0 && (
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h4 className="text-lg font-semibold mb-4">⚠️ Factores de Riesgo</h4>
            <div className="space-y-3">
              {risk.risk_factors.map((factor, idx) => (
                <div key={idx} className="flex items-start space-x-3 p-3 bg-gray-50 rounded">
                  <span className={`px-2 py-1 rounded text-xs font-semibold ${getRiskColor(factor.severity)}`}>
                    {factor.severity}
                  </span>
                  <div className="flex-1">
                    <div className="font-medium">{factor.category}</div>
                    <div className="text-sm text-gray-600">{factor.finding}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      Score Impact: {factor.score_impact}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Target Info */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h4 className="text-lg font-semibold mb-4">🎯 Objetivo Information</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Objetivo:</span>
              <span className="font-mono font-semibold">{selectedScan.target}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Scan Date:</span>
              <span>{new Date(selectedScan.created_at).toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Status:</span>
              <span className="px-2 py-1 bg-green-100 text-green-800 rounded">
                {selectedScan.status}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderPortScanTab = () => {
    if (!selectedScan?.results?.port_scan) return null;
    const portScan = selectedScan.results.port_scan;

    if (!portScan.success) {
      return (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">❌ Error: {portScan.error}</p>
        </div>
      );
    }

    const data = portScan.data;

    return (
      <div className="space-y-6">
        {/* Port Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600">Total Scanned</div>
            <div className="text-2xl font-bold">{data.total_scanned}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600">Open Ports</div>
            <div className="text-2xl font-bold text-blue-600">{data.total_open}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600">High Risk</div>
            <div className="text-2xl font-bold text-red-600">{data.high_risk_count}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600">Medium Risk</div>
            <div className="text-2xl font-bold text-yellow-600">{data.medium_risk_count}</div>
          </div>
        </div>

        {/* Open Ports Table */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="px-6 py-4 bg-gray-50 border-b">
            <h4 className="text-lg font-semibold">🔓 Open Ports Detected</h4>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Port</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Service</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Protocol</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Risk Level</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">State</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.open_ports && data.open_ports.length > 0 ? (
                  data.open_ports.map((port, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap font-mono font-semibold">
                        {port.port}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">{port.service}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                          {port.protocol}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${getRiskColor(port.risk_level)}`}>
                          {port.risk_level}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">
                          {port.state}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                      No open ports detected
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderSMBTab = () => {
    if (!selectedScan?.results?.smb_enumeration) return null;
    const smb = selectedScan.results.smb_enumeration;

if (!smb.success) {
  // Detectar si es error de herramienta faltante o servicio no disponible
const isToolMissing = smb.error && smb.error.includes('not installed');
  
  return (
    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
      <h4 className="text-lg font-semibold text-yellow-800 mb-2">
        {isToolMissing ? '⚠️ SMB Enumeration Not Available' : 'ℹ️ SMB Service Not Accessible'}
      </h4>
      <p className="text-yellow-700 mb-4">{smb.error}</p>
      {isToolMissing ? (
        <div className="bg-white rounded p-4 text-sm">
          <p className="font-semibold mb-2">📝 Nota:</p>
          <p className="text-gray-600">
            La enumeración SMB requiere herramientas adicionales instaladas en el servidor. 
            Esto es opcional y el scan puede continuar sin esta funcionalidad.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded p-4 text-sm">
          <p className="font-semibold mb-2">📝 Nota:</p>
          <p className="text-gray-600">
            El target no tiene el servicio SMB activo o no es accesible. 
            Esto es normal si el servidor no comparte archivos vía SMB/CIFS.
          </p>
        </div>
      )}
    </div>
  );
}

    const data = smb.data;

    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h4 className="text-lg font-semibold mb-4">🗂️ SMB Shares Found</h4>
          {data.shares && data.shares.length > 0 ? (
            <div className="space-y-3">
              {data.shares.map((share, idx) => (
                <div key={idx} className="border rounded-lg p-4 hover:bg-gray-50">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-mono font-semibold text-lg">{share.name}</div>
                      <div className="text-sm text-gray-600 mt-1">{share.type}</div>
                      {share.comment && (
                        <div className="text-sm text-gray-500 mt-1">💬 {share.comment}</div>
                      )}
                    </div>
                    {share.permissions && (
                      <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded text-sm">
                        {share.permissions}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">No SMB shares detected</p>
          )}
        </div>

        {data.anonymous_access !== undefined && (
          <div className={`rounded-lg shadow-lg p-6 ${
            data.anonymous_access ? 'bg-red-50 border-2 border-red-300' : 'bg-green-50 border-2 border-green-300'
          }`}>
            <h4 className="text-lg font-semibold mb-2">
              {data.anonymous_access ? '⚠️ Anonymous Access Enabled' : '✅ Anonymous Access Disabled'}
            </h4>
            <p className={data.anonymous_access ? 'text-red-700' : 'text-green-700'}>
              {data.anonymous_access 
                ? 'El servidor permite acceso anónimo a shares SMB. Esto representa un riesgo de seguridad.'
                : 'El servidor requiere autenticación para acceder a shares SMB. Esto es una buena práctica de seguridad.'
              }
            </p>
          </div>
        )}
      </div>
    );
  };

  const renderNFSTab = () => {
    if (!selectedScan?.results?.nfs_enumeration) return null;
    const nfs = selectedScan.results.nfs_enumeration;

if (!nfs.success) {
      const isToolMissing = nfs.error && nfs.error.includes('not installed');
      
      return (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <h4 className="text-lg font-semibold text-yellow-800 mb-2">
            {isToolMissing ? '⚠️ NFS Enumeration Not Available' : 'ℹ️ NFS Service Not Accessible'}
          </h4>
          <p className="text-yellow-700 mb-4">{nfs.error}</p>
          {isToolMissing ? (
            <div className="bg-white rounded p-4 text-sm">
              <p className="font-semibold mb-2">📝 Nota:</p>
              <p className="text-gray-600">
                La enumeración NFS requiere herramientas adicionales instaladas en el servidor. 
                Esto es opcional y el scan puede continuar sin esta funcionalidad.
              </p>
            </div>
          ) : (
            <div className="bg-white rounded p-4 text-sm">
              <p className="font-semibold mb-2">📝 Nota:</p>
              <p className="text-gray-600">
                El target no tiene el servicio NFS activo o no es accesible. 
                Esto es normal si el servidor no exporta sistemas de archivos vía NFS.
              </p>
            </div>
          )}
        </div>
      );
    }

    const data = nfs.data;

    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h4 className="text-lg font-semibold mb-4">📁 NFS Exports Found</h4>
          {data.exports && data.exports.length > 0 ? (
            <div className="space-y-3">
              {data.exports.map((exp, idx) => (
                <div key={idx} className="border rounded-lg p-4 hover:bg-gray-50">
                  <div className="font-mono font-semibold text-lg mb-2">{exp.path}</div>
                  {exp.allowed_hosts && exp.allowed_hosts.length > 0 && (
                    <div className="mt-2">
                      <div className="text-sm text-gray-600 mb-1">Allowed Hosts:</div>
                      <div className="flex flex-wrap gap-2">
                        {exp.allowed_hosts.map((host, hidx) => (
                          <span key={hidx} className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                            {host}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {exp.permissions && (
                    <div className="mt-2 text-sm">
                      <span className="text-gray-600">Permissions: </span>
                      <span className="font-mono">{exp.permissions}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">No NFS exports detected</p>
          )}
        </div>

        {data.world_writable && data.world_writable.length > 0 && (
          <div className="bg-red-50 border-2 border-red-300 rounded-lg p-6">
            <h4 className="text-lg font-semibold text-red-800 mb-2">🚨 World-Writable Exports Detected</h4>
            <p className="text-red-700 mb-3">
              Los siguientes exports tienen permisos world-writable. Esto representa un riesgo de seguridad crítico.
            </p>
            <div className="space-y-2">
              {data.world_writable.map((path, idx) => (
                <div key={idx} className="bg-white rounded p-2 font-mono text-sm">
                  {path}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Navigation Tabs */}
      <div className="bg-white rounded-lg shadow-lg p-4">
        <div className="flex space-x-4 border-b">
          <button
            onClick={() => setViewMode('form')}
            className={`px-4 py-2 font-medium ${
              viewMode === 'form'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            🔍 Nuevo Scan Network Enum
          </button>
          <button
            onClick={() => setViewMode('history')}
            className={`px-4 py-2 font-medium ${
              viewMode === 'history'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            📋 Historial ({scans.length})
          </button>
          {selectedScan && (
            <button
              onClick={() => setViewMode('results')}
              className={`px-4 py-2 font-medium ${
                viewMode === 'results'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              📊 Resultados
            </button>
          )}
        </div>
      </div>

      {/* Form View */}
      {viewMode === 'form' && (
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-6 text-gray-800">
            🌐 Network Enumeration Scanner
          </h2>
          <form onSubmit={startScan} className="space-y-6">
            <TargetSelect
              label="Objetivo (IP o Hostname)"
              value={target}
              onChange={setTarget}
              types={['domain', 'ip']}
              disabled={loading}
              helpText="Solo objetivos con autorización de escaneo vigente."
              testid="netenum-target-select"
            />

            <div className="border-t pt-4">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Opciones de Scan
              </label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={options.port_scan}
                    onChange={(e) => setOptions({...options, port_scan: e.target.checked})}
                    className="rounded text-blue-600 mr-2"
                  />
                  <span className="text-sm">🔓 Port Scanning (SMB, RPC, NFS, Databases)</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={options.smb_enum}
                    onChange={(e) => setOptions({...options, smb_enum: e.target.checked})}
                    className="rounded text-blue-600 mr-2"
                  />
                  <span className="text-sm">🗂️ SMB Share Enumeration</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={options.nfs_enum}
                    onChange={(e) => setOptions({...options, nfs_enum: e.target.checked})}
                    className="rounded text-blue-600 mr-2"
                  />
                  <span className="text-sm">📁 NFS Export Enumeration</span>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Timeout (segundos)
              </label>
              <input
                type="number"
                value={options.timeout}
                onChange={(e) => setOptions({...options, timeout: parseInt(e.target.value)})}
                min="1"
                max="30"
                className="w-32 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={loading}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`w-full py-3 px-4 rounded-lg font-medium text-white ${
                loading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {loading ? '🔄 Escaneando...' : '🚀 Iniciar Network Enumeration'}
            </button>
          </form>

          <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h4 className="font-semibold text-blue-800 mb-2">ℹ️ Sobre este módulo</h4>
            <p className="text-sm text-blue-700">
              Este scanner está diseñado para aseguradoras y auditorías de seguridad. 
              Realiza enumeración de servicios de red (SMB, NFS, RPC) de forma READ-ONLY 
              sin modificar configuraciones del servidor objetivo.
            </p>
          </div>
        </div>
      )}

      {/* History View */}
      {viewMode === 'history' && (
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-6 text-gray-800">
            📋 Historial de Network Enumeration Scans
          </h2>
          {scans.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <p className="text-lg mb-2">No hay scans registrados todavía</p>
              <p className="text-sm">Crea tu primer scan en la pestaña "Nuevo Scan"</p>
            </div>
          ) : (
            <div className="space-y-3">
              {scans.map((scan) => (
                <div
                  key={scan.id}
                  className="border rounded-lg p-4 hover:bg-gray-50 cursor-pointer"
                  onClick={() => viewScanDetails(scan)}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="font-mono font-semibold text-lg">{scan.target}</div>
                      <div className="text-sm text-gray-600 mt-1">
                        {new Date(scan.created_at).toLocaleString()}
                      </div>
                    </div>
                    <div className="flex flex-col items-end space-y-2">
                      <span className={`px-3 py-1 rounded text-sm font-semibold ${
                        scan.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {scan.status}
                      </span>
                      {scan.results?.risk_assessment && (
                        <span className={`px-3 py-1 rounded text-sm font-semibold ${
                          getRiskColor(scan.results.risk_assessment.risk_level)
                        }`}>
                          Risk: {scan.results.risk_assessment.risk_level}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Results View */}
      {viewMode === 'results' && selectedScan && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-gray-800">
                📊 Resultados: {selectedScan.target}
              </h2>
              <span className={`px-4 py-2 rounded font-semibold ${
                getRiskColor(selectedScan.results?.risk_assessment?.risk_level || 'INFO')
              }`}>
                {selectedScan.results?.risk_assessment?.risk_level || 'N/A'}
              </span>
            </div>

            {/* Results Tabs */}
            <div className="flex space-x-2 border-b overflow-x-auto">
              <button
                onClick={() => setActiveTab('summary')}
                className={`px-4 py-2 font-medium whitespace-nowrap ${
                  activeTab === 'summary'
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                📋 Summary
              </button>
              <button
                onClick={() => setActiveTab('ports')}
                className={`px-4 py-2 font-medium whitespace-nowrap ${
                  activeTab === 'ports'
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                🔓 Port Scan
              </button>
              <button
                onClick={() => setActiveTab('smb')}
                className={`px-4 py-2 font-medium whitespace-nowrap ${
                  activeTab === 'smb'
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                🗂️ SMB
              </button>
              <button
                onClick={() => setActiveTab('nfs')}
                className={`px-4 py-2 font-medium whitespace-nowrap ${
                  activeTab === 'nfs'
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                📁 NFS
              </button>
            </div>
          </div>

          {/* Tab Content */}
          <div>
            {activeTab === 'summary' && renderSummaryTab()}
            {activeTab === 'ports' && renderPortScanTab()}
            {activeTab === 'smb' && renderSMBTab()}
            {activeTab === 'nfs' && renderNFSTab()}
          </div>
        </div>
      )}
    </div>
  );
};

export default NetworkEnumScanner;
